import { useMemo } from 'react';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import {
  ONBOARDING_STEPS,
  SPECTRUM,
  TOTAL_STEPS,
  type OnboardingStepKey,
} from '../../constants/onboarding';
import { aboutMissing, canAdvance, type OnboardingAnswers } from '../../utils/onboardingFlow';
import { formatDateToLocal } from '../../utils/date';
import {
  CTA_GRADIENT,
  HAIRLINE,
  INK,
  OnboardingStepBody,
  stepBackground,
} from './OnboardingStepBody';

/**
 * The onboarding sequence — eight screens, run once after sign-in.
 *
 * Implements `docs/design/onboarding-v2/` (16 artboards: eight at 1280×900 and
 * the same eight at 390×844). This shell owns three things and nothing else:
 * the eyebrow line, the rail, and the controls. Each screen's headline, lede
 * and content belong to `OnboardingStepBody`, because in this design the
 * headline's size and position are part of the screen's argument.
 *
 * ⭐⭐ ONE OBJECT CARRIES THE WHOLE SEQUENCE. The eight-segment rail is full
 * size and empty on Welcome — a visible promise of eight — thins to a 2px
 * hairline under the eyebrow through the middle six, and returns full size at
 * the bottom of Ready with every segment lit. Same element throughout, both
 * breakpoints. It is what makes the ending feel earned rather than announced,
 * and it is why the rail MOVES between the header and the footer rather than
 * two different components being drawn in two places.
 *
 * ⚠️ TWO MODES, off the catalog's existing `teaching` flag. Teaching steps run
 * full-bleed; answering steps are a contained centre column, and the footer is
 * contained with them so the Continue button stays under the thing it commits.
 *
 * ⚠️ THIS SURFACE IS DARK IN EVERY THEME, deliberately: it is the only thing on
 * screen while it runs, so it paints explicit colours rather than theme tokens.
 * That also keeps it clear of `bg-primary` (which paints nothing without the
 * runtime `--primary-gradient`) and of the specificity fights in `theme.css`.
 *
 * ⚠️ `100dvh`, not `100vh`. Mobile browsers report `100vh` without the address
 * bar, so a full-height overlay is taller than the screen and its footer — the
 * Continue button — sits below the fold.
 */
export function OnboardingFlow() {
  const stepIndex = useOnboardingStore((s) => s.stepIndex);
  const answers = useOnboardingStore((s) => s.answers);
  const next = useOnboardingStore((s) => s.next);
  const back = useOnboardingStore((s) => s.back);

  const step = ONBOARDING_STEPS[stepIndex - 1] ?? ONBOARDING_STEPS[0];
  // ⚠️ Same clock the store's `next()` uses — the module itself reads none.
  const canGo = canAdvance(step.key, answers, formatDateToLocal(new Date()));

  /* The two bookends. Derived from the ends of the sequence rather than a list
     of step names, which would be a second place to update. */
  const bookend = stepIndex === 1 || stepIndex === TOTAL_STEPS;

  const ctaText = useMemo(() => {
    switch (step.key) {
      case 'welcome': return 'Begin';
      case 'consents': return 'Agree and continue';
      case 'ready': return 'Enter Metanoia';
      default: return 'Continue';
    }
  }, [step.key]);

  /* ⚠️ One line, three jobs — and the disabled case is the one that matters. A
     dead Continue button with no explanation reads as a broken app, and this
     one blocks on three different steps for three different reasons. */
  const hint = useMemo(() => {
    if (!canGo) return blockedReason(step.key, answers, formatDateToLocal(new Date()));
    if (step.key === 'welcome') return 'About two minutes.';
    if (step.key === 'motivation') {
      const n = answers.reasons.length;
      return `${n} chosen`;
    }
    return '';
  }, [canGo, step.key, answers]);

  /* Answering steps are a contained column. ⚠️ The HEADER and the FOOTER stay
     full width across every step — the rail and the two controls are the one
     part of this sequence that must not move as you go through it. Narrowing
     the footer with the column it commits was the first build's idea and it
     made Back and Continue jump between steps 5 and 6. */
  const column = step.teaching
    ? 'w-full'
    : step.key === 'about'
      ? 'mx-auto w-full max-w-[640px]'
      : 'mx-auto w-full max-w-[780px]';

  const rail = <Rail stepIndex={stepIndex} prominent={bookend} />;

  return (
    <div
      className="fixed inset-0 z-[60] overflow-hidden"
      style={{
        height: '100dvh',
        background: stepBackground(step.key),
        /* The notch and the home indicator. The design's 68px top margin on the
           phone is this inset plus the container's own 20px. */
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Metanoia"
    >
      <div className="mx-auto flex h-full w-full max-w-[1440px] flex-col px-[22px] pb-[30px] pt-5 lg:px-14 lg:pb-11 lg:pt-[42px] xl:px-[88px]">

        {/* ── Header: who you are being asked, and where you are ─────────── */}
        <div className="flex shrink-0 flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-4">
            <span
              className="truncate text-[10px] uppercase tracking-[.18em] lg:text-[11px] lg:tracking-[.2em]"
              style={{ color: INK.faint }}
            >
              {step.eyebrow}
            </span>
            <span
              className="shrink-0 text-[10px] tabular-nums tracking-[.08em] lg:text-[11px]"
              style={{ color: INK.faint }}
            >
              {pad(stepIndex)} / {pad(TOTAL_STEPS)}
            </span>
          </div>
          {!bookend && rail}
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        {/* ⚠️ `min-h-0` on the flex child that scrolls. A flex child defaults to
            `min-height:auto` and refuses to shrink below its content, so
            without it the body pushes the footer off a short screen instead of
            scrolling — the vertical failure this app keeps hitting. */}
        <main className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${column}`}>
          <OnboardingStepBody stepKey={step.key} />
        </main>

        {/* ── Footer: the rail on the bookends, then the controls ─────────── */}
        <div className="flex w-full shrink-0 flex-col gap-3.5">
          {bookend && <div className="pt-4">{rail}</div>}

          {hint && (
            <p className="m-0 text-center text-[12px] lg:hidden" style={{ color: INK.faint }}>
              {hint}
            </p>
          )}

          <div
            className={`flex items-center gap-2.5 ${bookend ? '' : 'lg:border-t lg:pt-6'}`}
            style={bookend ? undefined : { borderColor: HAIRLINE }}
          >
            {stepIndex > 1 && (
              <button
                type="button"
                onClick={back}
                /* ⚠️ border and background as CLASSES, not inline. Inline styles
                   out-rank utilities, so `lg:bg-transparent` was silently doing
                   nothing and the phone's pill stayed painted behind the
                   desktop link. */
                className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[.03] transition-colors hover:bg-white/[.07] lg:flex lg:h-auto lg:w-auto lg:gap-2 lg:border-0 lg:bg-transparent lg:px-1 lg:py-3 lg:text-sm lg:hover:bg-transparent lg:hover:text-white"
                style={{ color: INK.muted }}
              >
                <ChevronLeft className="h-[18px] w-[18px] lg:h-4 lg:w-4" />
                <span className="hidden lg:inline">Back</span>
              </button>
            )}

            {hint && (
              <span className="hidden text-[13px] lg:block" style={{ color: INK.dim }}>
                {hint}
              </span>
            )}

            <button
              type="button"
              onClick={next}
              disabled={!canGo}
              className="ml-auto flex h-[52px] flex-1 items-center justify-center gap-2.5 rounded-xl px-7 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed lg:h-auto lg:flex-none lg:py-3.5"
              style={{
                background: canGo ? CTA_GRADIENT : 'rgba(255,255,255,.06)',
                color: canGo ? '#04222B' : INK.dim,
              }}
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The sequence, as one object.
 *
 * ⚠️ The lit segments are {@link SPECTRUM} read STRAIGHT, not sampled through
 * `spectrumAt`: there are eight steps and seven act colours, and the eighth
 * segment is white on purpose — the sequence finishing is not another act, it
 * is the point where the colours are all in.
 */
function Rail({ stepIndex, prominent }: { stepIndex: number; prominent: boolean }) {
  return (
    <div className={`flex ${prominent ? 'gap-1.5' : 'gap-1'}`} aria-hidden="true">
      {ONBOARDING_STEPS.map((s, i) => (
        <div
          key={s.key}
          className={`flex-1 rounded-full transition-colors duration-300 ${prominent ? 'h-1' : 'h-[2px]'}`}
          style={{ background: i < stepIndex ? railColour(i) : 'rgba(255,255,255,.07)' }}
        />
      ))}
    </div>
  );
}

const railColour = (i: number): string => (i < SPECTRUM.length ? SPECTRUM[i] : '#FFFFFF');

const pad = (n: number): string => String(n).padStart(2, '0');

/** ⚠️ `about` now names WHICH of its three requirements is outstanding. */
function blockedReason(key: OnboardingStepKey, answers?: OnboardingAnswers, today?: string): string {
  switch (key) {
    case 'motivation': return 'Choose at least one.';
    case 'about': {
      const missing = answers && today ? aboutMissing(answers, today) : [];
      if (missing.length === 0) return 'Tell us what to call you.';
      const list = missing.length === 1
        ? missing[0]
        : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
      return `Still needed: ${list}.`;
    }
    case 'consents': return 'The two required agreements are needed to continue.';
    default: return '';
  }
}
